import { filter, fromEvent, race, take, takeUntil } from 'rxjs';
import { elementMove } from '../../cdk/rxjs';
import { DRAWER_SIZE_CLASSES, type DrawerSize } from '../drawer/drawer.types';
import type { ActiveDialog } from './active-dialog';

export const DIALOG_BACKDROP_CLASS = 'backdrop:bg-black/[.25]';
export const CENTER_CLASSES =
  'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 m-0';

const drawerOpts = {
  position: 'right' as 'left' | 'right',
  size: 'large' as DrawerSize,
  dismissable: true,
  backdrop: true,
  title: '',
  titleFn: undefined as (() => string) | undefined,
  stickyContent: false,
  showCloseButton: false,
};

type DrawerOptions = Partial<typeof drawerOpts>;

export class DialogBuilder<Data> {
  #opts: DialogOptions = {};

  constructor(
    private readonly nativeElement: HTMLDialogElement,
    readonly instance: ActiveDialog<Data>,
  ) {
    nativeElement.className = `rounded-xl ${CENTER_CLASSES} ${DIALOG_BACKDROP_CLASS} shadow-hovering `;
    this.#setupAnimations();
  }

  #setupAnimations() {
    // Inject global animation styles for all dialogs
    if (!document.getElementById('tvd-dialog-animations')) {
      const style = document.createElement('style');
      style.id = 'tvd-dialog-animations';
      style.textContent = `
        dialog.tvd-animated {
          transition: transform 0.2s ease, opacity 0.2s ease,
                      display 0.2s allow-discrete, overlay 0.2s allow-discrete;
          opacity: 0;
          transform: scale(0.7);
        }

        dialog.tvd-animated::backdrop {
          transition: opacity 0.2s ease,
                      display 0.2s allow-discrete, overlay 0.2s allow-discrete;
          opacity: 0;
        }

        dialog.tvd-animated[open] {
          opacity: 1;
          transform: scale(1);
        }

        dialog.tvd-animated[open]::backdrop {
          opacity: 1;
        }

        @starting-style {
          dialog.tvd-animated[open] {
            opacity: 0;
            transform: scale(0.7);
          }

          dialog.tvd-animated[open]::backdrop {
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          dialog.tvd-animated,
          dialog.tvd-animated::backdrop {
            transition: none !important;
            transform: none !important;
            opacity: 1 !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.nativeElement.classList.add('tvd-animated');
  }

  open() {
    this.#handleCloseOnEscape();
    if (this.#opts.dismissable !== false) {
      this.#closeOnClick();
    }
    if (this.#opts.backdrop === false) {
      this.nativeElement.classList.replace(
        DIALOG_BACKDROP_CLASS,
        'backdrop:bg-transparent',
      );
    }

    this.#addCustomClasses();

    if (this.#opts.mobileFullscreen !== false) {
      const dialog = this.nativeElement;
      dialog.classList.add(
        'w-screen',
          'mx-auto',
          'md:max-w-auto',
          'md:max-h-auto',
          'md:h-fit',
          'md:w-fit',
      );
    }

    if (this.#opts.draggable) {
      this.nativeElement.classList.add('cursor-grab');

      this.#makeDraggable();
    }

    if (this.#opts.asModal === false) {
      document.body.style.overflow = 'auto';
      this.nativeElement.show();
    } else {
      this.nativeElement.showModal();
    }
    return this.instance;
  }

  withConfig(opts: DialogOptions) {
    this.#opts = opts;

    return this;
  }

  asDrawer(opts: DrawerOptions = drawerOpts): DrawerDialogBuilder<Data> {
    return new DrawerDialogBuilder(this.nativeElement, this.instance, opts);
  }

  #addCustomClasses() {
    let classes = this.#opts.customClasses;
    if (classes) {
      if (typeof classes === 'string') {
        classes = [classes];
      }
      const container = this.nativeElement.firstElementChild as HTMLElement;
      container.classList.add(...classes);
    }
  }

  #makeDraggable() {
    elementMove(this.nativeElement, {
      start: () => {
        this.nativeElement.style.cursor = 'grabbing';
      },
      end: () => {
        this.nativeElement.style.cursor = 'grab';
      },
    }).subscribe(({ deltaX, deltaY }) => {
      this.nativeElement.style.left = `${deltaX}px`;
      this.nativeElement.style.top = `${deltaY}px`;
    });
  }

  #closeOnClick() {
    fromEvent(this.nativeElement, 'mousedown')
      .pipe(
        filter((e) => e.target === this.nativeElement),
        take(1),
      )
      .subscribe(() => this.instance.close());
  }

  #handleCloseOnEscape() {
    return race(
      fromEvent(this.nativeElement, 'cancel'),
      fromEvent<KeyboardEvent>(this.nativeElement, 'keydown').pipe(
        filter((e) => e.key === 'Escape'),
      ),
    )
      .pipe(takeUntil(this.instance.result))
      .subscribe((e) => {
        if (this.#opts.dismissable === false) {
          e.preventDefault();
          return;
        }
        this.instance.close();
      });
  }
}
export class DrawerDialogBuilder<Data> {
  #isClosing = false;

  constructor(
    private readonly nativeElement: HTMLDialogElement,
    readonly instance: ActiveDialog<Data>,
    private readonly opts: DrawerOptions,
  ) {
    this.opts = {
      ...drawerOpts,
      ...opts,
    };
    this.#setupDrawerAnimations();
    this.#applyDrawerClasses();
    this.#interceptClose();
  }

  #interceptClose() {
    // Override the instance.close to animate before closing
    const originalClose = this.instance.close.bind(this.instance);
    this.instance.close = (payload?: Data) => {
      if (this.#isClosing) return;
      this.#isClosing = true;

      // Add closing class to trigger reverse animation
      this.nativeElement.classList.add('tvd-drawer-closing');

      // Wait for animation to complete
      this.nativeElement.addEventListener(
        'transitionend',
        () => {
          this.nativeElement.classList.remove('tvd-drawer-closing');
          originalClose(payload);
        },
        { once: true },
      );
    };
  }

  #setupDrawerAnimations() {
    // Inject global drawer animation styles
    if (!document.getElementById('tvd-drawer-animations')) {
      const style = document.createElement('style');
      style.id = 'tvd-drawer-animations';
      style.textContent = `
        dialog.tvd-drawer-left,
        dialog.tvd-drawer-right {
          height: 100vh !important;
          max-height: 100vh !important;
          transition: transform 300ms cubic-bezier(0.25, 0.8, 0.25, 1),
                      display 300ms allow-discrete,
                      overlay 300ms allow-discrete;
          opacity: 1;
        }

        dialog.tvd-drawer-left::backdrop,
        dialog.tvd-drawer-right::backdrop {
          transition: opacity 300ms cubic-bezier(0.25, 0.8, 0.25, 1),
                      display 300ms allow-discrete,
                      overlay 300ms allow-discrete;
          opacity: 0;
        }

        dialog.tvd-drawer-left {
          transform: translateX(-100%);
        }

        dialog.tvd-drawer-right {
          transform: translateX(100%);
        }

        dialog.tvd-drawer-left[open],
        dialog.tvd-drawer-right[open] {
          transform: translateX(0);
        }

        dialog.tvd-drawer-left[open]::backdrop,
        dialog.tvd-drawer-right[open]::backdrop {
          opacity: 1;
        }

        /* Closing animations */
        dialog.tvd-drawer-left.tvd-drawer-closing {
          transform: translateX(-100%);
        }

        dialog.tvd-drawer-right.tvd-drawer-closing {
          transform: translateX(100%);
        }

        dialog.tvd-drawer-left.tvd-drawer-closing::backdrop,
        dialog.tvd-drawer-right.tvd-drawer-closing::backdrop {
          opacity: 0;
        }

        @starting-style {
          dialog.tvd-drawer-left[open] {
            transform: translateX(-100%);
          }

          dialog.tvd-drawer-right[open] {
            transform: translateX(100%);
          }

          dialog.tvd-drawer-left[open]::backdrop,
          dialog.tvd-drawer-right[open]::backdrop {
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          dialog.tvd-drawer-left,
          dialog.tvd-drawer-right,
          dialog.tvd-drawer-left::backdrop,
          dialog.tvd-drawer-right::backdrop {
            transition: none !important;
            transform: none !important;
            opacity: 1 !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  #applyDrawerClasses() {
    // Clear initial classes completely and start fresh
    this.nativeElement.className = '';

    // Add drawer positioning classes
    const isLeft = this.opts.position === 'left';
    const positionClass = isLeft ? 'left-0' : 'right-0';
    const borderClass = isLeft ? 'border-r' : 'border-l';
    const sizeClass = DRAWER_SIZE_CLASSES[this.opts.size || 'medium'];
    const animationClass = `tvd-drawer-${this.opts.position}`;

    this.nativeElement.classList.add(
      'fixed',
      'top-0',
      'h-full',
      'w-full',
      positionClass,
      sizeClass,
      'max-sm:min-w-full',
      'rounded-none',
      'm-0',
      'bg-card',
      borderClass,
      'border-card-border',
      'shadow-xl',
      animationClass,
      DIALOG_BACKDROP_CLASS,
    );

    // Explicitly set positioning via inline styles to override any conflicts
    if (isLeft) {
      this.nativeElement.style.left = '0';
      this.nativeElement.style.right = 'auto';
    } else {
      this.nativeElement.style.right = '0';
      this.nativeElement.style.left = 'auto';
    }

    // Remove max-w constraints from container and ensure it fills the drawer
    const container = this.nativeElement.firstElementChild as HTMLElement;
    if (container) {
      container.classList.remove(
        'md:max-w-[664px]',
        'max-h-[85vh]',
        'p-8',
        'w-full',
      );
      container.style.height =
        'calc(100dvh - env(safe-area-inset-bottom, 0px))';

      // Configure container based on stickyContent option
      if (this.opts.stickyContent) {
        // Sticky content mode: let child component manage scrolling
        container.classList.add(
          'flex',
          'flex-col',
          'h-full',
          '!py-0',
          'px-6',
          'overflow-hidden',
        );
      } else {
        // Default mode: container handles scrolling
        container.classList.add(
          'h-full',
          'max-h-full',
          'px-6',
          'overflow-y-auto',
        );
      }
    }

    // Add header if title is provided
    if (this.opts.title) {
      this.#addHeader(container);
    }

    // Add standalone close button (absolutely positioned, no layout impact)
    if (this.opts.showCloseButton && !this.opts.title) {
      this.#addCloseButton(container);
    }
  }

  #addHeader(container: HTMLElement) {
    // Create header container
    const header = document.createElement('div');
    const headerClasses = 'flex items-center justify-between';
    header.className = this.opts.stickyContent
      ? `${headerClasses} flex-shrink-0 shadow-md sticky top-0 bg-card z-10 -mx-6 px-6 py-4`
      : `${headerClasses} py-4`;

    // Create title if provided
    const titleValue = this.opts.titleFn
      ? this.opts.titleFn()
      : this.opts.title;
    if (titleValue) {
      const titleContainer = document.createElement('div');
      titleContainer.className = 'flex items-center space-x-2';
      const title = document.createElement('h2');
      title.className = 'text-lg font-semibold';
      title.textContent = titleValue;

      // Store title element reference on ActiveDialog for dynamic updates
      this.instance.setTitleElement(title);

      titleContainer.appendChild(title);
      header.appendChild(titleContainer);
    } else {
      // Add empty div to maintain flex layout when no title
      const emptyDiv = document.createElement('div');
      header.appendChild(emptyDiv);
    }

    // Insert header at the beginning of container
    container.insertBefore(header, container.firstChild);

    // Create and insert divider only if title is provided
    if (this.opts.title) {
      const divider = document.createElement('div');
      divider.className = this.opts.stickyContent
        ? 'h-px bg-card-border flex-shrink-0 -mx-6'
        : 'h-px bg-card-border';
      container.insertBefore(divider, header.nextSibling);
    }

    // Adjust content wrapper based on mode
    const contentIndex = this.opts.title ? 2 : 1;
    const content = container.children[contentIndex] as HTMLElement;
    if (content) {
      if (this.opts.stickyContent) {
        // Sticky mode: content fills remaining space, child manages layout
        content.classList.add('flex-1', 'min-h-0');
      } else {
        // Default mode: add padding
        content.classList.add('py-4');
      }
    }
  }

  #addCloseButton(container: HTMLElement) {
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className =
      'absolute top-4 right-4 z-20 flex items-center justify-center w-8 h-8 hover:bg-background-100 rounded-full transition-colors cursor-pointer';
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.12L10.59 12 5.7 16.89a1 1 0 0 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 1 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4Z"/></svg>';
    closeBtn.addEventListener('click', () => this.instance.close());
    container.style.position = 'relative';
    container.appendChild(closeBtn);
  }

  open(): ActiveDialog<Data> {
    this.#handleCloseOnEscape();

    if (this.opts.dismissable !== false) {
      this.#closeOnClick();
    }

    if (this.opts.backdrop === false) {
      this.nativeElement.classList.replace(
        DIALOG_BACKDROP_CLASS,
        'backdrop:bg-transparent',
      );
    }

    this.nativeElement.showModal();
    return this.instance;
  }

  #closeOnClick() {
    fromEvent(this.nativeElement, 'mousedown')
      .pipe(
        filter((e) => e.target === this.nativeElement),
        take(1),
      )
      .subscribe(() => this.instance.close());
  }

  #handleCloseOnEscape() {
    return race(
      fromEvent(this.nativeElement, 'cancel'),
      fromEvent<KeyboardEvent>(this.nativeElement, 'keydown').pipe(
        filter((e) => e.key === 'Escape'),
      ),
    )
      .pipe(takeUntil(this.instance.result))
      .subscribe((e) => {
        if (this.opts.dismissable === false) {
          e.preventDefault();
          return;
        }
        this.instance.close();
      });
  }
}

type DialogOptions = {
  dismissable?: boolean;
  backdrop?: boolean;
  asModal?: boolean;
  customClasses?: string | string[];
  mobileFullscreen?: boolean;
  draggable?: boolean;
};
